import React from "react";
import { isMobile } from "react-device-detect";
import Skeleton from "react-loading-skeleton";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import getFundamentalistChartsData from "./getChartsData";

/**
 * Card containing all financial chart
 */
export function FinancialChartsCard({ analysisData, assetType }) {
  if (!analysisData) {
    return skeletonLoading();
  }

  if (typeof analysisData === "object" && assetType === 1) {
    const chartsData = getFundamentalistChartsData(analysisData);

    if (chartsData) {
      return (
        <div className="media-body animated fadeInDown ht-100p">
          <div
            className="card card-minimal-three chart"
            style={{
              borderRadius: 10,
              boxShadow: "0 0 10px rgb(28 39 60 / 10%)",
              borderColor: "var(--border-4)",
            }}
          >
            <div className="card-header">
              <h4>Dados Financeiros</h4>
              <p>
                <span>
                  Acompanhe os dados fundamentalistas de uma empresa de maneira
                  simples e fácil.
                </span>
              </p>
            </div>
            <div className="card-body ht-100p">
              <div>{getResultsDemonstrationChart(chartsData)}</div>

              {
                //Only show row with cols in Desktop version.
                isMobile ? (
                  <div className="mg-t-20-OnlyMobile">
                    {getCashFlowChart(chartsData)}

                    {getPatrimonyBalanceChart(chartsData)}
                  </div>
                ) : (
                  <div className="row mg-t-10">
                    <div className="col-md">{getCashFlowChart(chartsData)}</div>
                    <div className="col-md">
                      {getPatrimonyBalanceChart(chartsData)}
                    </div>
                  </div>
                )
              }
            </div>
          </div>
        </div>
      );
    }
  }
  return null;
}

export const FinancialChartCardMemo = React.memo(FinancialChartsCard);

const chartStyle = () => {
  if (isMobile) {
    return { width: "100%", height: 250 };
  } else {
    return { width: "100%", height: 225 };
  }
};

const getResultsDemonstrationChart = (data) => {
  if (data && data.resultsDemonstrations) {
    return (
      <div>
        <h5 className="pd-l-20 pd-t-10">Demonstrações</h5>
        <div style={chartStyle()}>
          <ResponsiveContainer>
            <ComposedChart
              data={data.resultsDemonstrations}
              margin={{
                top: 10,
                right: 10,
                bottom: 10,
                left: 10,
              }}
            >
              <CartesianGrid stroke="var(--stroke)" />
              <XAxis dataKey="name" scale="band" />
              <YAxis />
              <Tooltip contentStyle={{ borderRadius: 10, backgroundColor: "var(--bg-10)", borderColor: "var(--border)" }} />

              <Area
                type="monotone"
                dataKey="clearProfit"
                name="Lucro Líquido"
                fill="#8884d8"
                stroke="#8884d8"
              />
              <Line
                type="monotone"
                dataKey="grossProfit"
                name="Lucro Bruto"
                stroke="#8d6e63"
              />
              <Bar
                dataKey="operationalReceipt"
                name="Receita Operacional"
                barSize={20}
                fill="#0277bd"
              />
              <Bar
                dataKey="totalReceipt"
                name="Receita Total"
                barSize={20}
                fill="#413ea0"
              />

              <Legend wrapperStyle={{ height: 5 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

const getCashFlowChart = (data) => {
  if (data && data.cashFlow) {
    return (
      <div>
        <h5 className="pd-l-20 pd-t-10">Fluxo de caixa</h5>
        <div style={chartStyle()}>
          <ResponsiveContainer>
            <ComposedChart
              data={data.cashFlow}
              margin={{
                top: 10,
                right: 10,
                bottom: 10,
                left: 10,
              }}
            >
              <CartesianGrid stroke="var(--stroke)" />
              <XAxis dataKey="name" scale="band" />
              <YAxis />
              <Tooltip contentStyle={{ borderRadius: 10, backgroundColor: "var(--bg-10)", borderColor: "var(--border)" }} />

              <Legend />
              <Area
                type="monotone"
                dataKey="liquidVariations"
                name="Variações Liquidas"
                fill="#a5d6a7"
                stroke="#a5d6a7"
              />
              <Bar
                dataKey="operationalActivity"
                name="Operacionais"
                barSize={15}
                fill="#413ea0"
              />
              <Bar
                dataKey="investimentsActivity"
                name="Investimentos"
                barSize={15}
                fill="#6a1b9a"
              />
              <Line
                type="monotone"
                dataKey="financingActivity"
                name="Financiamentos"
                stroke="#8d6e63"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

const getPatrimonyBalanceChart = (data) => {
  if (data && data.patrimonyBalance) {
    return (
      <div>
        <h5 className="pd-l-20 pd-t-10">Balanço Patrimonial</h5>
        <div style={chartStyle()}>
          <ResponsiveContainer>
            <ComposedChart
              data={data.patrimonyBalance}
              margin={{
                top: 10,
                right: 10,
                bottom: 10,
                left: 10,
              }}
            >
              <CartesianGrid stroke="var(--stroke)" />
              <XAxis dataKey="name" scale="band" />
              <YAxis />
              <Tooltip contentStyle={{ borderRadius: 10, backgroundColor: "var(--bg-10)", borderColor: "var(--border)" }} />

              <Legend />
              <Area
                type="monotone"
                dataKey="totalLiquid"
                name="Patrimonio Liquido"
                fill="#8884d8"
                stroke="#8884d8"
              />
              <Bar
                dataKey="totalPassive"
                name="Passivos"
                barSize={20}
                fill="#0277bd"
              />
              <Bar
                dataKey="totalActive"
                name="Ativos"
                barSize={20}
                fill="#413ea0"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

const skeletonLoading = () => {
  return (
    <div className="media-body ht-100p animated fadeInDown">
      <div
        className="card card-minimal-three chart"
        style={{
          borderRadius: 10,
          boxShadow: "0 0 10px rgb(28 39 60 / 10%)",
          borderColor: "var(--border-4)",
        }}
      >
        <div className="card-header">
          <h4>Dados Financeiros</h4>
          <p>
            <span>
              Acompanhe os dados fundamentalistas de uma empresa de maneira
              simples e fácil.
            </span>
          </p>
        </div>
        <div className="card-body">
          <div style={{ padding: 30 }}>
            <Skeleton count={9} style={{ marginTop: 10 }} />
          </div>
        </div>
      </div>
    </div>
  );
};
